import React, { useState, useEffect, useRef } from "react";
import "./css/Header.css";
import { useNotification } from "./context/NotificationContext";
import { useTheme } from "./context/ThemeContext";
import { useUser } from "./context/UserContext";
import { useSubscription } from "./context/SubscriptionContext";
import { useRoleCheck } from "../../hooks/useRoleCheck";
import useFeatureAccess from "../../hooks/useFeatureAccess";
import HeaderAppDesktop from "./HeaderAppDesktop";
import HeaderAppMobile from "./HeaderAppMobile";

function Header({
  toggleModal,
  toggleCreateAccountModal,
  handleHomeClick,
  setHeroContent,
  toggleChangePasswordModal,
  heroContent,
}) {
  const { theme, setTheme, effectiveTheme } = useTheme();
  const {
    loggedInUser,
    logoutUser,
    selectedUser,
    selectUser,
    exitReadOnlyView,
    isReadOnlyView,
  } = useUser();

  const handleSwitchBack = () => {
    if (setHeroContent) setHeroContent("Member User Info");
    exitReadOnlyView();
    selectUser(loggedInUser);
  };
  const { hasActiveSubscription, isLoading: subscriptionLoading } =
    useSubscription();

  // Feature access control - combines user activation and subscription checks
  const { canAccessFunctionalFeatures, getAccessDenialReason, isDeactivated } =
    useFeatureAccess();

  // Role checking for admin functionality
  const {
    canAccessAccountManagement,
    canManageGroups,
    isPlatformAdmin,
    canManageUsers,
  } = useRoleCheck(loggedInUser);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Mobile menu section states
  const [isMobileScamDetectExpanded, setIsMobileScamDetectExpanded] =
    useState(false);
  const [isMobileSimulationsExpanded, setIsMobileSimulationsExpanded] =
    useState(false);
  const [isMobileEducationExpanded, setIsMobileEducationExpanded] =
    useState(false);
  const [isMobileReportsExpanded, setIsMobileReportsExpanded] = useState(false);
  const [isMobilePlatformAdminExpanded, setIsMobilePlatformAdminExpanded] =
    useState(false);
  const [isMobileDemoExpanded, setIsMobileDemoExpanded] = useState(false);

  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const { unreadCount } = useNotification();

  // Calculate user name from UserContext (first name only for cleaner display)
  const userName = loggedInUser ? loggedInUser.firstName : "";

  // Check if user can see Account menu item
  const canSeeAccountMenu = canAccessAccountManagement();

  // Check if user can see group information
  const canSeeGroupInfo = canManageGroups();

  // Check if user can see Subscription and Payment menu items (admin only)
  const canSeeSubscriptionMenu = canAccessAccountManagement();

  // All authenticated users can access the Users menu to view their own info
  // The MemberUserInfo component will restrict which sections are visible based on roles
  const canSeeUsersMenu = isAuthenticated;

  // Update authentication status based on loggedInUser from UserContext
  useEffect(() => {
    console.log(
      "Header: loggedInUser changed, updating auth status:",
      loggedInUser,
    );
    setIsAuthenticated(!!loggedInUser);
    // NOTE: Group user count is set in localStorage by LoginFormModal on login.
    // No need for a separate API call here — avoids duplicate my-group/count request.
  }, [loggedInUser]);

  // Monitor localStorage for sign-in modal trigger from SidePanel
  useEffect(() => {
    const checkSignInModalFlag = () => {
      const shouldShowModal = localStorage.getItem("showSignInModal");
      if (shouldShowModal === "true") {
        localStorage.removeItem("showSignInModal");
        toggleModal("login");
      }
    };

    // Check on mount and when storage changes
    checkSignInModalFlag();
    window.addEventListener("storage", checkSignInModalFlag);

    // Also check periodically in case same-tab localStorage changes aren't caught
    const interval = setInterval(checkSignInModalFlag, 500);

    return () => {
      window.removeEventListener("storage", checkSignInModalFlag);
      clearInterval(interval);
    };
  }, [toggleModal]);

  const toggleUserDropdown = () => {
    setShowUserDropdown(!showUserDropdown);
  };

  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  const handleClick = (e) => {
    e.preventDefault();
    const href = e.currentTarget.getAttribute("href");
    console.log("🖱️ Header: handleClick called with href:", href);

    if (href === "#backtomain") {
      console.log(
        "🏠 Header: Back to Main Site clicked - calling handleHomeClick",
      );
      handleHomeClick();
    } else if (href === "#signin") {
      toggleModal("login");
    } else if (href === "#signup") {
      toggleCreateAccountModal(true);
    } else if (href === "#dashboard") {
      setHeroContent("Dashboard");
    } else if (href === "#howdoi") {
      if (canAccessFunctionalFeatures) {
        setHeroContent("How do I...?");
      }
      // Do nothing if no access - link is disabled
    } else if (href === "#gettingstarted") {
      if (canAccessFunctionalFeatures) {
        setHeroContent("Getting Started");
      }
    } else if (href === "#notifications") {
      setHeroContent("Notifications");
    }

    setShowUserDropdown(false);
    setShowMobileMenu(false);
  };

  const handleAccountClick = () => {
    if (canSeeAccountMenu) {
      setHeroContent("Account & Billing");
    }
    setShowUserDropdown(false);
  };

  const handleAccountProfileClick = () => {
    if (canSeeAccountMenu) {
      setHeroContent("Account Profile");
    }
    setShowUserDropdown(false);
    setShowMobileMenu(false);
  };

  const handleSettingsClick = () => {
    setHeroContent("Settings");
    setShowUserDropdown(false);
  };

  const handleUsersClick = () => {
    console.log("Header: Users menu clicked, navigating to Member User Info");
    setHeroContent("Member User Info");
    setShowUserDropdown(false);
  };

  // Subscriptions & Payments now route to unified Account & Billing page
  const handleSubscriptionsClick = () => {
    setHeroContent("Account & Billing");
    setShowUserDropdown(false);
  };

  const handlePaymentsClick = () => {
    setHeroContent("Account & Billing");
    setShowUserDropdown(false);
  };

  const handleSignOutClick = () => {
    // Explicit user-initiated logout — bypass the post-login grace window
    // so the click works even if the user just logged in seconds ago.
    logoutUser({ force: true, reason: "user-initiated" });
    setHeroContent("Welcome");
    setShowUserDropdown(false);
    setShowMobileMenu(false);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target)
      ) {
        setShowMobileMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Common props for both desktop and mobile headers
  const commonProps = {
    isAuthenticated,
    userName,
    canSeeAccountMenu,
    canSeeGroupInfo,
    canSeeUsersMenu,
    canSeeSubscriptionMenu,
    isPlatformAdmin,
    handleClick,
    setHeroContent,
    handleAccountClick,
    handleAccountProfileClick,
    handleSettingsClick,
    handleUsersClick,
    handleSubscriptionsClick,
    handlePaymentsClick,
    handleSignOutClick,
    theme,
    setTheme,
    unreadCount,
    canAccessFunctionalFeatures,
    getAccessDenialReason,
    isDeactivated,
    hasActiveSubscription,
    selectedUser,
    selectUser,
    loggedInUser,
    handleSwitchBack,
    isReadOnlyView,
    heroContent,
  };

  return (
    <div className="header" data-theme={effectiveTheme}>
      <div className="logo-container">
        <img
          src={process.env.REACT_APP_MAIN_APP_LOGO || "/KinShield.png"}
          alt="Logo"
          className="logo"
          draggable={false}
          onContextMenu={(e) => e.preventDefault()}
        />
        <span
          className="app-name mobile-clickable"
          onClick={(e) => {
            // Only handle click on mobile screens
            if (window.innerWidth <= 768) {
              console.log(
                "🏠 Header: KinShield™ clicked on mobile - calling handleHomeClick",
              );
              handleHomeClick();
            }
          }}
          style={{ cursor: window.innerWidth <= 768 ? "pointer" : "default" }}
        >
          KinShield™
        </span>
      </div>

      <HeaderAppDesktop
        {...commonProps}
        showUserDropdown={showUserDropdown}
        dropdownRef={dropdownRef}
        toggleUserDropdown={toggleUserDropdown}
      />

      <HeaderAppMobile
        {...commonProps}
        showMobileMenu={showMobileMenu}
        mobileMenuRef={mobileMenuRef}
        toggleMobileMenu={toggleMobileMenu}
        isMobileScamDetectExpanded={isMobileScamDetectExpanded}
        setIsMobileScamDetectExpanded={setIsMobileScamDetectExpanded}
        isMobileSimulationsExpanded={isMobileSimulationsExpanded}
        setIsMobileSimulationsExpanded={setIsMobileSimulationsExpanded}
        isMobileEducationExpanded={isMobileEducationExpanded}
        setIsMobileEducationExpanded={setIsMobileEducationExpanded}
        isMobileReportsExpanded={isMobileReportsExpanded}
        setIsMobileReportsExpanded={setIsMobileReportsExpanded}
        isMobilePlatformAdminExpanded={isMobilePlatformAdminExpanded}
        setIsMobilePlatformAdminExpanded={setIsMobilePlatformAdminExpanded}
        isMobileDemoExpanded={isMobileDemoExpanded}
        setIsMobileDemoExpanded={setIsMobileDemoExpanded}
      />
    </div>
  );
}

export default Header;
